/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  inject,
  Injector,
  input,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormsModule, NgControl } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FileHelper, ManagedFile } from '../../../helperts/file.helper';
import { CustomErrorDisplayComponent } from '../custom-error-display/custom-error-display.component';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { UploadStore } from '../../../store/upload';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PopupService } from '../../../services/popup.service';
import { PopupComponent } from '../../popup/popup.component';
import { CustomCropperComponent } from '../custom-cropper/custom-cropper.component';
import { v4 as uuidv4 } from 'uuid';

export type UploadProgressMap = { [fileId: string]: { progress: number; status: string } };

@Component({
  selector: 'lib-custom-file-input',
  standalone: true,
  imports: [
    CommonModule,
    CustomErrorDisplayComponent,
    FormsModule,
    TranslateModule,
    PopupComponent,
    CustomCropperComponent,
  ],
  templateUrl: './custom-file-input.component.html',
  styles: [
    `
      .upload-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(255, 255, 255, 0.8);
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 0.375rem; /* rounded-md */
        z-index: 20;
        transition: opacity 0.3s ease-in-out;
      }

      .progress-circle {
        width: 5rem; /* 80px */
        height: 5rem; /* 80px */
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
      }

      /* This creates the white inner circle, making the outer one look like a ring */
      .progress-circle::before {
        content: '';
        position: absolute;
        width: 4rem; /* 64px */
        height: 4rem; /* 64px */
        background: #fff;
        border-radius: 50%;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      }

      .progress-text {
        position: relative; /* To appear above the ::before pseudo-element */
        z-index: 1;
        font-size: 1.125rem; /* text-lg */
        font-weight: 600; /* font-semibold */
        color: #374151; /* gray-700 */
      }

      .status-icon {
        font-size: 3rem; /* text-5xl */
        display: flex;
        align-items: center;
        justify-content: center;
        width: 5rem;
        height: 5rem;
      }

      .status-icon.success {
        color: #22c55e; /* green-500 */
      }

      .status-icon.error {
        color: #ef4444; /* red-500 */
      }

      .status-icon i {
        /* Optional: add a slight drop shadow to the icon for better visibility */
        filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1)) drop-shadow(0 1px 1px rgba(0, 0, 0, 0.06));
      }
    `,
  ],
})
export class CustomFileInputComponent
  implements ControlValueAccessor, OnInit, AfterViewInit, OnDestroy
{
  label = input<string>('');
  displayType = input<string>('circle');
  accept = input<string>('*');
  id = input<string>('');
  name = input<string>('');
  errors = input<{ [key: string]: string }>({});
  displayError = input<boolean>(true);
  height = input<string>('');
  wrapLabel = input<boolean>(false);

  // Propiedades para el modal de vista previa único
  previewModalId = 'file-preview-modal';
  cropperModalId = 'file-cropper-modal';
  currentPreviewFile: ManagedFile | null = null;

  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;

  managedFiles: ManagedFile[] = [];
  isDraggingOver = false;
  isDisabled = false;
  ngControl: NgControl | null = null;

  minFilesInput = input<number>(1);
  maxFilesInput = input<number>(1);

  minFiles = signal<number>(1);
  maxFiles = signal<number>(1);

  useCropper = input<boolean>(false);
  aspectRatio = input<number>(1);

  cropperFile: File | null = null;

  private readonly _sanitizer = inject(DomSanitizer);
  private readonly _injector = inject(Injector);
  private readonly _store = inject(Store);
  private readonly _destroy$ = new Subject<void>();
  private readonly _popupService = inject(PopupService);

  /**
   * Genera un ID único para los modales
   * @returns string ID único
   */
  private _generateUniqueId(): string {
    return uuidv4();
  }

  private _uploads: UploadProgressMap = {};

  onChange: (value: ManagedFile[]) => void = () => {};
  onTouched: () => void = () => {};

  get isInvalid(): boolean {
    if (!this.ngControl?.control) {
      return false;
    }
    const { invalid, touched, dirty } = this.ngControl.control;
    return !!(invalid && (touched || dirty));
  }

  ngOnInit(): void {
    this.minFiles.set(this.minFilesInput());
    this.maxFiles.set(this.maxFilesInput());
    // Generar IDs únicos para los modales
    this.previewModalId = `file-preview-modal-${this._generateUniqueId()}`;
    this.cropperModalId = `file-cropper-modal-${this._generateUniqueId()}`;

    // Get the ngControl from the injector
    this.ngControl = this._injector.get(NgControl, null);
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    // Extract min and max validators if present
    if (this.ngControl?.control) {
      this._extractMinMaxValidators(this.ngControl.control);
    }

    // Subscribe to upload progress
    this._store
      .select(UploadStore.selectors.selectUploads)
      .pipe(takeUntil(this._destroy$))
      .subscribe((uploads) => {
        this._uploads = uploads;
      });
  }

  ngAfterViewInit(): void {
    // We need to get the control here and extract validators.
    // Defer with setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError.
    const control = this.ngControl?.control;
    if (control) {
      setTimeout(() => this._extractMinMaxValidators(control));
    }
  }

  async writeValue(value: unknown | unknown[] | null): Promise<void> {
    this.managedFiles = [];
    if (!value) {
      return;
    }

    const sources = Array.isArray(value) ? value : [value];
    const promises = sources.map((source) => {
      const sourceAsManagedFile = source as ManagedFile;
      if (typeof source === 'string') {
        return Promise.resolve(FileHelper.createManagedFileFromUrl(source, this._sanitizer));
      }
      if (source instanceof File) {
        return FileHelper.createManagedFileFromFile(source, this._sanitizer, false);
      }
      if (sourceAsManagedFile?.url && typeof sourceAsManagedFile.url === 'string') {
        return Promise.resolve(
          FileHelper.createManagedFileFromUrl(
            sourceAsManagedFile.url,
            this._sanitizer,
            sourceAsManagedFile.metadata
          )
        );
      }
      if (sourceAsManagedFile?.file && sourceAsManagedFile.file instanceof File) {
        return FileHelper.createManagedFileFromFile(
          sourceAsManagedFile.file,
          this._sanitizer,
          false,
          undefined,
          sourceAsManagedFile.metadata
        );
      }
      return Promise.resolve(null);
    });

    const results = await Promise.all(promises);
    this.managedFiles = results.filter((f): f is ManagedFile => f !== null);
    // Do not emit change from writeValue to prevent circular updates
  }

  registerOnChange(fn: (value: ManagedFile[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  getUploadProgress(file: ManagedFile): number | null {
    const fileId = file.id;
    if (!this._uploads || !fileId || !this._uploads[fileId]) {
      return null;
    }

    const uploadState = this._uploads[fileId];
    if (uploadState.status !== 'in-progress' || typeof uploadState.progress !== 'number') {
      return null;
    }

    return uploadState.progress;
  }

  getUploadStatus(file: ManagedFile): string | null {
    const fileId = file.id;
    if (!this._uploads || !fileId || !this._uploads[fileId]) {
      return null;
    }
    return this._uploads[fileId].status;
  }

  @HostListener('dragover', ['$event'])
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.isDisabled) {
      this.isDraggingOver = true;
    }
  }

  @HostListener('dragleave', ['$event'])
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = false;
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = false;
    if (!this.isDisabled && event.dataTransfer?.files) {
      this._handleFiles(event.dataTransfer.files);
    }
  }

  onFileSelected(event: Event): void {
    const element = event.target as HTMLInputElement;
    if (element.files) {
      if (this.useCropper()) {
        this.openCropper(element.files?.[0]);
      } else {
        this._handleFiles(element.files);
      }
    }
    element.value = '';
  }

  openCropper(file: File): void {
    this.cropperFile = file;
  }

  removeFile(index: number, event: MouseEvent): void {
    event.stopPropagation();
    this.managedFiles = this.managedFiles.filter((_, i) => i !== index);
    this._emitChange();
  }

  /**
   * Downloads a file using its URL
   * @param file The ManagedFile to download
   * @param event The mouse event
   */
  downloadFile(file: ManagedFile, event: MouseEvent): void {
    event.stopPropagation();
    if (file.url) {
      // Create a temporary anchor element
      const link = document.createElement('a');
      link.href = file.url;
      link.download = file.fileInfo.name || 'download';
      link.target = '_blank';

      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  triggerFileInputClick(): void {
    if (!this.isDisabled) {
      this.fileInputRef.nativeElement.click();
    }
  }

  /**
   * Checks if a file is previewable based on its extension
   * @param file The ManagedFile to check
   * @returns boolean indicating if the file can be previewed
   */
  isPreviewable(file: ManagedFile | null): boolean {
    if (!file || !file.fileInfo) return false;

    const { extension } = file.fileInfo;

    return [
      ...FileHelper.IMAGE_EXTENSIONS,
      ...FileHelper.PDF_EXTENSIONS,
      ...FileHelper.VIDEO_EXTENSIONS,
      ...FileHelper.AUDIO_EXTENSIONS,
    ].includes(extension);
  }

  /**
   * Checks if a file is an image
   * @param file The ManagedFile to check
   * @returns boolean indicating if the file is an image
   */
  isImage(file: ManagedFile | null): boolean {
    if (!file || !file.fileInfo) return false;
    return FileHelper.IMAGE_EXTENSIONS.includes(file.fileInfo.extension);
  }

  /**
   * Checks if a file is a PDF
   * @param file The ManagedFile to check
   * @returns boolean indicating if the file is a PDF
   */
  isPdf(file: ManagedFile | null): boolean {
    if (!file || !file.fileInfo) return false;
    return FileHelper.PDF_EXTENSIONS.includes(file.fileInfo.extension);
  }

  /**
   * Checks if a file is a video
   * @param file The ManagedFile to check
   * @returns boolean indicating if the file is a video
   */
  isVideo(file: ManagedFile | null): boolean {
    if (!file || !file.fileInfo) return false;
    return FileHelper.VIDEO_EXTENSIONS.includes(file.fileInfo.extension);
  }

  /**
   * Checks if a file is an audio file
   * @param file The ManagedFile to check
   * @returns boolean indicating if the file is an audio file
   */
  isAudio(file: ManagedFile | null): boolean {
    if (!file || !file.fileInfo) return false;
    return FileHelper.AUDIO_EXTENSIONS.includes(file.fileInfo.extension);
  }

  /**
   * Opens a preview modal for the file
   * @param file The ManagedFile to preview
   * @param event The mouse event
   */
  previewFile(file: ManagedFile, event: MouseEvent): void {
    event.stopPropagation();
    if (!file.url) return;

    // Establecer el archivo actual para la vista previa
    this.currentPreviewFile = file;

    // Abrir el modal único con el nombre del archivo como título
    this._popupService.open(this.previewModalId, file.fileInfo.name, 'lg');
  }

  closePreview(): void {
    this._popupService.close(this.previewModalId);
    this.currentPreviewFile = null;
  }

  /**
   * Sanitizes a URL to make it safe for use in DOM elements
   * @param url The URL to sanitize
   * @returns A safe URL that can be used in DOM elements
   */
  getSafeUrl(url: string | undefined): SafeResourceUrl {
    if (!url) return this._sanitizer.bypassSecurityTrustResourceUrl('');

    // For PDFs, use Google Docs Viewer to ensure they display in the browser
    if (url.toLowerCase().endsWith('.pdf')) {
      // Use Google Docs Viewer for PDFs
      return this._sanitizer.bypassSecurityTrustResourceUrl(
        `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`
      );
    }
    return this._sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  getIconForFile(file: ManagedFile): string {
    return FileHelper.getIconForExtension(file.fileInfo.extension);
  }

  getPlaceholderIcon(): string {
    const acc = this.accept()?.toLowerCase() || '';
    if (acc.includes('image')) {
      return 'fa-solid fa-image';
    }
    if (acc.includes('pdf')) {
      return 'fa-solid fa-file-pdf';
    }
    if (acc.includes('xml')) {
      return 'fa-solid fa-file-code';
    }
    return 'fa-solid fa-file-lines';
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private async _handleFiles(files: FileList): Promise<void> {
    if (!files.length) {
      return;
    }

    const isMultiple = !!this.maxFiles() && this.maxFiles() > 1;
    let filesToProcess = Array.from(files);

    // If not in multiple mode, we will only process one file.
    if (!isMultiple) {
      filesToProcess = [filesToProcess[0]];
    }

    // If a max limit is set, ensure we don't exceed it.
    if (this.maxFiles() !== null && this.maxFiles() > 0) {
      const remainingSlots = this.maxFiles() - this.managedFiles.length;
      if (remainingSlots <= 0) {
        return; // No more files can be added.
      }
      // Only take as many files as there are remaining slots.
      filesToProcess = filesToProcess.slice(0, remainingSlots);
    }

    if (!filesToProcess.length) {
      return;
    }

    const promises = filesToProcess.map((file) =>
      FileHelper.createManagedFileFromFile(file, this._sanitizer, true)
    );
    const newManagedFiles = (await Promise.all(promises)).filter((f): f is ManagedFile => !!f);

    // If in multiple mode, append. Otherwise, replace.
    if (isMultiple) {
      this.managedFiles = [...this.managedFiles, ...newManagedFiles];
    } else {
      this.managedFiles = newManagedFiles;
    }

    this._emitChange();
  }

  private _emitChange(): void {
    this.onChange(this.managedFiles);
    this.onTouched();
  }

  private _extractMinMaxValidators(control: AbstractControl): void {
    // Min
    const errorsForShort: any = control.validator?.({ value: [1] } as any);
    const min = errorsForShort?.minlength?.requiredLength ?? 0;

    // Max
    const errorsForLong: any = control.validator?.({
      value: new Array(this.maxFiles()).fill(1),
    } as any);

    const max = errorsForLong?.maxlength?.requiredLength ?? this.maxFiles();

    this.minFiles.set(min);
    this.maxFiles.set(max);
  }

  onCroppedFile(file: File): void {
    this._handleFiles([file] as any);
    this.cropperFile = null;
  }

  onCropCanceled(): void {
    this.cropperFile = null;
  }
}
